/**
 * VideoUploadNode (Canvas V2)
 *
 * Upload a local video file. Supports file picker and drag-and-drop.
 * 300MB limit, backend API upload, VideoPreviewPlayer for preview.
 * FloatingNodeLabel (editable) above the node, hidden output port.
 *
 * Uses a custom card wrapper matching ImageUploadNode's clean,
 * media-focused card style with semantic rings for state indication.
 * No BaseNode header bar — just the video content.
 *
 * 6 visual states:
 *   1. Empty — clickable label, upload icon + "Upload Video"
 *   2. Uploading — video preview + overlay spinner
 *   3. Processing — opaque spinner-only card
 *   4. Error — error icon + message + retry hint
 *   5. Success — video (VideoPreviewPlayer) + always-visible Download button
 *   6. Hover — additionally reveals the secondary expand + replace buttons
 *
 * Output: out_video (video)
 */

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react'
import { Loader2 } from 'lucide-react'
import { NODE_CARD_RADIUS_CLASS, isPortraitAspectRatio } from '../shared/constants'
import { useTranslation } from 'react-i18next'
import { NodeProps, Position } from 'reactflow'
import { createPortal } from 'react-dom'
import type { NodeDataRecord } from '../../nodeRegistry'
import {
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  UploadMediaNodeToolbar,
  VideoPreviewPlayer,
  type VideoPreviewPlayerHandle,
  useExtractAudioAction,
  useExtractFrameAction,
  ASPECT_WIDTH_MAP,
  useRefreshMediaNodeInternals,
  useDepthVideoAction,
  generatedVideoPath,
  getPickedIndex,
  resolveNodeErrorMessage,
} from '../shared'
import { TypedHandle } from '../../TypedHandle'
import { useCanvasOptional } from '../../CanvasContext'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { useFileUpload } from '../shared/useFileUpload'
import {
  recordDecodedMediaDimensions,
  reservedMediaCacheKey,
  resolveReservedMediaBox,
} from '../shared/reservedMediaBox'
import { useNodeActions } from '../shared/useNodeActions'
import { useIsPreparingC2paDownload } from '../shared/c2paDownloadProgress'
import {
  useUpscaleVideoAction,
  videoDimensionsFromMetadata,
  type VideoDimensions,
} from '../shared/useUpscaleVideoAction'
import { api } from '../../../../api/client'
import { getVideoPosterUrl } from '../../../../api/useAssetUrl'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createNadDebug } from '../../../../utils/debugLog'

import { dataStatus,dataStr,dataStrOpt } from '../../../../hooks/useYCanvas'
import { type ReactFlowCanvasNodeDataByType } from '../../../../generated/nodeDataTypes'
import { useRegisterNodeContextAction } from '../../contextMenu/nodeContextActions'

type VideoUploadNodeData = ReactFlowCanvasNodeDataByType['video']

export interface VideoUploadNodeShellHandle {
  id: string
  portType: string
  position: Position
  top?: string
  hidden?: boolean
}

interface VideoUploadNodeShellProps {
  cardProps?: HTMLAttributes<HTMLDivElement>
  cardRef?: Ref<HTMLDivElement>
  cardTestId?: string
  children: ReactNode
  fallbackName: string
  handles: readonly VideoUploadNodeShellHandle[]
  hasError?: boolean
  id: string
  name: string
  nodeWidth: number
  onRename?: (name: string) => void
  selected?: boolean
  toolbar: ReactNode
  typeIcon?: ReactNode
}

/** Canonical canvas shell shared by uploaded and generated video assets. */
export function VideoUploadNodeShell({
  cardProps,
  cardRef,
  cardTestId,
  children,
  fallbackName,
  handles,
  hasError = false,
  id,
  name,
  nodeWidth,
  onRename,
  selected,
  toolbar,
  typeIcon = NODE_TYPE_ICON.video,
}: VideoUploadNodeShellProps) {
  const nodePresentationWidth = useNodePresentationWidth()
  const {
    className: cardClassName = '',
    style: cardStyle,
    ...restCardProps
  } = cardProps ?? {}

  return (
    <div
      className="relative"
      data-testid="video-upload-node-shell"
      style={{ width: nodePresentationWidth(nodeWidth) }}
    >
      {toolbar}
      <FloatingNodeLabel
        name={name}
        fallback={fallbackName}
        typeIcon={typeIcon}
        onRename={onRename}
      />
      <div
        ref={cardRef}
        data-testid={cardTestId}
        className={`canvas-node-card-contained ${NODE_CARD_RADIUS_CLASS} overflow-hidden ${
          selected
            ? 'ring-2 ring-primary'
            : hasError
              ? 'ring-1 ring-destructive'
              : ''
        } ${cardClassName}`}
        style={{
          width: nodePresentationWidth(nodeWidth),
          ...cardStyle,
        }}
        {...restCardProps}
      >
        {children}
      </div>
      {handles.map((handle) => (
        <TypedHandle
          key={`${handle.position}-${handle.id}`}
          nodeId={id}
          type={handle.position === Position.Left ? 'target' : 'source'}
          position={handle.position}
          id={handle.id}
          portType={handle.portType}
          size={handle.hidden ? 2 : undefined}
          style={{
            ...(handle.top ? { top: handle.top } : {}),
            ...(handle.hidden
              ? {
                  opacity: 0,
                  border: 'none',
                  background: 'transparent',
                  pointerEvents: 'none',
                }
              : {}),
          }}
        />
      ))}
    </div>
  )
}

const aspectRatioLog = createNadDebug('dw:portrait-video-node-aspect-ratio:dimensions')
// Feature: persistent (non-hover) download button on the exported video node.
const persistentDownloadLog = createNadDebug('dw:video-export-persistent-download')
const DEPTH_VIDEO_PENDING_MODE = 'depth_video_pending'

const VideoUploadNode = memo(({ id, data, selected, dragging }: NodeProps<VideoUploadNodeData>) => {
  const { t } = useTranslation('canvas')
  const { previewStyle, mediaFit } = useNodePresentationPreview()
  const status = dataStatus(data)
  const error = dataStrOpt(data, 'error')

  const canvas = useCanvasOptional()
  const node = useCanvasNode(id, 'video', data)
  const projectId = useProjectId()
  const { handleDownload: sharedDownload } = useNodeActions({ nodeId: id })
  const downloadPreparing = useIsPreparingC2paDownload(id)
  const [isHovering, setIsHovering] = useState(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [loadedVideoDimensions, setLoadedVideoDimensions] = useState<{
    path: string
    dimensions: VideoDimensions
  } | null>(null)

  // Suspend canvas keyboard shortcuts while this overlay is open (see canvasShortcutGuard).
  useBlockingOverlay(isVideoModalOpen)

  const out_video = node?.get('out_video')
  const in_file_name = node?.get('in_file_name')

  // Track whether the label was manually set by the user
  const labelManuallySet = useRef(false)

  // Local state for immediate label display (Issue 2: frontend-origin YDoc
  // writes skip React state rebuild, so we need local state for instant feedback)
  const [derivedLabel, setDerivedLabel] = useState<string>('')

  const updateNodeData = useCallback(
    (updates: NodeDataRecord<'video'>) => {
      // Auto-set label from uploaded filename (if not manually renamed)
      const merged = { ...updates }
      if (updates.in_file_name && typeof updates.in_file_name === 'string' && !labelManuallySet.current) {
        const nameWithoutExt = updates.in_file_name.replace(/\.[^.]+$/, '')
        merged.label = nameWithoutExt
        setDerivedLabel(nameWithoutExt)
      }
      // Write schema-declared fields via typed node handle
      if (merged.in_file_name !== undefined && typeof merged.in_file_name === 'string') {
        node?.writeInFileName(merged.in_file_name)
      }
      // Write remaining non-schema fields via canvas
      canvas?.updateNodeData({ id, type: 'video' }, merged)
    },
    [id, canvas, node]
  )

  const {
    previewUrl,
    isUploading,
    handleFileChange,
    dragHandlers,
    isDragOver,
    retry,
    multiFileWarning,
    uploadFile,
  } = useFileUpload({
    nodeId: id,
    acceptMime: 'video',
    updateNodeData,
    persistedValue: out_video,
    initialPreviewUrl: dataStrOpt(data, '_initialPreviewUrl'),
  })

  // Auto-upload initialFile when canvas context is ready
  const initialFile = data.initialFile instanceof File ? data.initialFile : undefined
  const initialFileProcessed = useRef(false)
  useEffect(() => {
    if (initialFile && canvas && !initialFileProcessed.current) {
      initialFileProcessed.current = true
      uploadFile(initialFile)
    }
  }, [initialFile, canvas, uploadFile])

  // Clear initialFile from node data after processing
  const initialFileCleared = useRef(false)
  useEffect(() => {
    if (initialFile && !initialFileCleared.current) {
      initialFileCleared.current = true
      setTimeout(() => {
        canvas?.updateNodeData({ id, type: 'video' }, { initialFile: undefined })
      }, 100)
    }
  }, [initialFile, id, canvas])

  const outVideoHistory = out_video?.history ?? []
  const selectedVideo = outVideoHistory[getPickedIndex(out_video)]
  const outVideoPath = generatedVideoPath(selectedVideo)
  const sourceDimensions =
    loadedVideoDimensions && loadedVideoDimensions.path === outVideoPath
      ? loadedVideoDimensions.dimensions
      : videoDimensionsFromMetadata(selectedVideo?.result?.metadata)
  const isDepthVideoPending = selectedVideo?.metadata?.mode === DEPTH_VIDEO_PENDING_MODE
  const videoPreviewRef = useRef<VideoPreviewPlayerHandle>(null)
  const extractFrameAction = useExtractFrameAction({
    nodeId: id,
    hasVideo: Boolean(outVideoPath),
    videoPreviewRef,
  })
  const extractAudioAction = useExtractAudioAction({
    nodeId: id,
    sourceNodeType: 'video',
    hasVideo: status === 'completed' && !isUploading && Boolean(outVideoPath),
  })
  const depthVideoAction = useDepthVideoAction({ nodeId: id, hasVideo: Boolean(outVideoPath) })
  const { action: upscaleVideoAction, flyout: upscaleVideoFlyout } = useUpscaleVideoAction({
    nodeId: id,
    hasVideo: Boolean(outVideoPath),
    isBusy:
      isUploading ||
      status === 'pending_execution' ||
      status === 'pending_cost_confirmation' ||
      status === 'running',
    sourceName: derivedLabel || dataStr(data, 'label') || in_file_name,
    sourceDimensions,
    sourceVideoPath: outVideoPath,
  })
  const persistedUrl = outVideoPath ? api.getAssetUrl(projectId, outVideoPath) : undefined
  const displayUrl = previewUrl || persistedUrl
  // First-frame poster (RFC-0931) only for the persisted asset — not the local
  // blob preview during upload. Null → VideoPreviewPlayer keeps today's <video>.
  const posterUrl = !previewUrl && outVideoPath ? getVideoPosterUrl(projectId, outVideoPath) : null

  // Reserved box from persisted metadata / the session decode cache — the same
  // resolver the parent group's computeCellSize uses, so a viewport-culling
  // remount renders the decoded size immediately instead of a 16:9 placeholder
  // that would re-layout the group (see reservedMediaBox.ts).
  const [decodeRevision, bumpDecodeRevision] = useReducer((n: number) => n + 1, 0)
  const reservedBox = useMemo(
    () => resolveReservedMediaBox(previewUrl ? undefined : out_video, previewUrl, projectId),
    // decodeRevision is the cache-invalidation signal for the module-level cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewUrl, out_video, projectId, decodeRevision],
  )
  const aspectRatio = displayUrl && reservedBox.source !== 'schema-default' ? reservedBox.aspectRatio : null
  // Node width snapped to closest ASPECT_WIDTH_MAP bucket; default 16:9 = 315px
  const nodeWidth = displayUrl ? reservedBox.width : ASPECT_WIDTH_MAP['16:9']
  useRefreshMediaNodeInternals({
    nodeId: id,
    projectId,
    mediaKind: 'video',
    nodeWidth,
    aspectRatio,
    hasMedia: !!displayUrl,
  })

  /** Apply authoritative natural media dimensions to the canvas card geometry. */
  const handleLoadedDimensions = useCallback(
    (width: number, height: number) => {
      if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return

      const cacheKey = previewUrl || reservedMediaCacheKey(out_video)
      if (!recordDecodedMediaDimensions(projectId, cacheKey, width, height)) return
      bumpDecodeRevision()
      const applied = resolveReservedMediaBox(previewUrl ? undefined : out_video, previewUrl, projectId)
      aspectRatioLog({
        event: 'video-upload-dimensions-applied',
        trace_id: `portrait-video-node-aspect-ratio:${id}`,
        project_id: projectId,
        node_id: id,
        natural_width: width,
        natural_height: height,
        aspect_ratio: applied.aspectRatio,
        node_width: applied.width,
        box_source: applied.source,
        poster_available: Boolean(posterUrl),
      })
    },
    [id, posterUrl, projectId, previewUrl, out_video],
  )

  const handleLoadedVideoDimensions = useCallback(
    (width: number, height: number) => {
      if (!outVideoPath) return
      setLoadedVideoDimensions({
        path: outVideoPath,
        dimensions: { width, height },
      })
    },
    [outVideoPath],
  )

  const handleDownload = useCallback(() => {
    persistentDownloadLog({
      event: 'download-clicked',
      trace_id: `video-export-persistent-download:${id}`,
      project_id: projectId,
      node_id: id,
      has_video: Boolean(outVideoPath),
    })
    if (!outVideoPath) return
    sharedDownload(outVideoPath, in_file_name || 'video.mp4')
  }, [id, projectId, outVideoPath, in_file_name, sharedDownload])
  useRegisterNodeContextAction(id, 'download', handleDownload, Boolean(outVideoPath))

  // Escape to close lightbox
  useEffect(() => {
    if (!isVideoModalOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setIsVideoModalOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isVideoModalOpen])

  const handleRename = useCallback(
    (newName: string) => {
      labelManuallySet.current = true
      setDerivedLabel(newName)
      canvas?.updateNodeData({ id, type: 'video' }, { label: newName })
    },
    [id, canvas]
  )

  const inputId = `file-upload-${id}`
  const isError = status === 'error'
  const isProcessing =
    status === 'pending_execution' ||
    status === 'running' ||
    (isDepthVideoPending && status !== 'completed')
  const hasError = isError && !!error
  const resolvedError = hasError ? resolveNodeErrorMessage(error, t) : undefined

  return (
    <>
      <VideoUploadNodeShell
        id={id}
        nodeWidth={nodeWidth}
        selected={selected}
        hasError={hasError}
        name={derivedLabel || dataStr(data, 'label') || ''}
        fallbackName={t('videoUploadNode.untitled')}
        onRename={handleRename}
        toolbar={
          <UploadMediaNodeToolbar
            nodeId={id}
            selected={selected}
            dragging={dragging}
            extraActions={[
              extractFrameAction,
              extractAudioAction,
              depthVideoAction,
              upscaleVideoAction,
            ]}
            originalName={derivedLabel || dataStr(data, 'label') || t('videoUploadNode.untitled')}
          />
        }
        handles={[
          {
            id: 'in_transform_video',
            portType: 'video',
            position: Position.Left,
            hidden: true,
          },
          {
            id: 'out_video',
            portType: 'video',
            position: Position.Right,
            hidden: true,
          },
        ]}
        cardProps={dragHandlers}
      >
          {/* Hidden file input */}
          <input
            type="file"
            accept="video/*"
            onChange={handleFileChange}
            className="hidden"
            id={inputId}
            disabled={isUploading}
          />

          {/* Main content area -- 6 visual states */}
          {isUploading && displayUrl ? (
            /* -- State 2: Uploading -- video preview + overlay spinner -- */
            <div
              className="relative"
              style={{ aspectRatio: aspectRatio || '16/9', overflow: 'hidden', ...previewStyle }}
            >
              <video
                src={displayUrl}
                style={{ objectFit: mediaFit }}
                className="w-full h-full object-cover block"
                muted
                playsInline
              />
              <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="text-xxs text-white/80 mt-2">{t('videoUploadNode.uploadingVideo')}</span>
              </div>
            </div>
          ) : hasError ? (
            /* -- State 4: Error -- icon + message + retry -- */
            <div
              className="relative w-full bg-canvas flex flex-col items-center justify-center px-6 cursor-pointer"
              style={{ aspectRatio: '16/9', ...previewStyle }}
              onClick={retry}
            >
              <svg
                className="w-8 h-8 text-foreground/25"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
              <span className="text-xs font-medium text-foreground/50 mt-1.5">{t('videoUploadNode.clickToRetry')}</span>
              {/* Status overlay — the shared, neutral treatment (post 1634117708345526). */}
              <NodeStatusMessage
                message={resolvedError ?? t('videoUploadNode.failedToUpload')}
                testId="upload-card-error"
              />
            </div>
          ) : isProcessing ? (
            /* -- State 3: Processing -- never expose the seeded source video -- */
            <div
              data-testid="video-processing-overlay"
              role="status"
              aria-live="polite"
              className="flex w-full flex-col items-center justify-center bg-background"
              style={{ aspectRatio: aspectRatio || '16/9', ...previewStyle }}
            >
              <Loader2
                aria-hidden="true"
                className="h-6 w-6 animate-spin text-foreground"
                strokeWidth={2}
              />
              <span className="mt-2 text-sm font-medium text-foreground">
                {t('videoUploadNode.processingVideo')}
              </span>
            </div>
          ) : displayUrl ? (
            /* -- States 5 + 6: Success + Hover -- VideoPreviewPlayer with controls -- */
            <div
              className="relative"
              style={{ aspectRatio: aspectRatio || '16/9', overflow: 'hidden', ...previewStyle }}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {/*
                All overlay controls come from VideoPreviewPlayer's single
                top-right cluster: [mute, Replace, Expand, Download]. This node
                used to hand-roll its OWN absolutely-positioned cluster in the
                same corner, which meant it duplicated the Expand/Download
                buttons and pushed the player's mute toggle into a different
                corner from every other video surface. "Replace" is the only
                genuinely node-specific control, so it is passed through
                `extraControls`.

                Download stays ALWAYS visible (post 1579949463762351): on an
                uploaded/exported video node it is the primary action and must
                be discoverable without hovering.
              */}
              <VideoPreviewPlayer
                ref={videoPreviewRef}
                mediaFit={mediaFit}
                videoUrl={displayUrl}
                posterUrl={posterUrl}
                isHovering={isHovering}
                showExpand
                showDownload
                downloadPreparing={downloadPreparing}
                downloadAlwaysVisible
                overlayDirection={aspectRatio && isPortraitAspectRatio(aspectRatio) ? 'column' : 'row'}
                onExpand={() => setIsVideoModalOpen(true)}
                onDownload={handleDownload}
                extraControls={
                  /* Replace -- a <label>, not a button: it triggers the hidden
                     file input via htmlFor. */
                  <label
                    htmlFor={inputId}
                    className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors cursor-pointer nodrag"
                    title={t('videoUploadNode.replace')}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </label>
                }
                onLoadedDimensions={handleLoadedDimensions}
                onLoadedVideoDimensions={handleLoadedVideoDimensions}
              />
              {/* RFC-1612: signing-for-download progress, in the status slot. */}
              {downloadPreparing && (
                <NodeDownloadPreparingBanner className={isHovering ? 'bottom-9' : undefined} />
              )}
            </div>
          ) : (
            /* -- State 1: Empty -- clickable upload area -- */
            <label
              htmlFor={inputId}
              className={`w-full bg-canvas flex flex-col items-center justify-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'bg-primary/5 ring-2 ring-primary/40 ring-inset'
                  : ''
              }`}
              style={{ aspectRatio: '16/9', ...previewStyle }}
            >
              {isDragOver ? (
                <span className="text-xs font-medium text-primary">{t('videoUploadNode.dropToUpload')}</span>
              ) : (
                <>
                  <svg
                    className="w-8 h-8 text-foreground/15"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-xxs text-foreground/30 mt-1">{t('videoUploadNode.uploadVideo')}</span>
                </>
              )}
            </label>
          )}

          {/* Multi-file drop warning */}
          {multiFileWarning && (
            <div className="px-2 py-1.5 bg-amber-500/10 border-t border-amber-500/30">
              <span className="text-xxs text-amber-400">{multiFileWarning}</span>
            </div>
          )}
      </VideoUploadNodeShell>

      {/* Video Lightbox */}
      {isVideoModalOpen && displayUrl &&
        createPortal(
          <div
            data-testid="video-lightbox-backdrop"
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <div
              data-testid="video-lightbox"
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="video-lightbox-close"
                onClick={() => setIsVideoModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <video
                src={displayUrl}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
              />
            </div>
          </div>,
          document.body,
        )}
      {upscaleVideoFlyout}
    </>
  )
})

VideoUploadNode.displayName = 'VideoUploadNode'
export default VideoUploadNode
import { useNodePresentationWidth, useNodePresentationPreview } from '../../NodePresentationContext'
import { NodeDownloadPreparingBanner } from '../shared/NodeDownloadPreparingBanner'
import { NodeStatusMessage } from '../shared/NodeStatusMessage'
